import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/site/Bits";
import { Reveal } from "@/components/site/Reveal";
import { Check, ArrowUpRight, ArrowRight, Sparkles, Nfc as NfcIcon } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const YEAR_FACTOR = 12 * 0.85; // -15% sur l'annuel

export default function Pricing() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const features = t("pricing.features");

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const priceFor = (p) => (cycle === "monthly" ? p.price_monthly : p.price_monthly * YEAR_FACTOR);
  const fmt = (v) => v.toFixed(2).replace(".", ",");

  return (
    <div>
      <PageHero eyebrow={t("pricing.eyebrow")} title={t("pricing.title")} subtitle={t("pricing.subtitle")} />

      {/* ===== ABONNEMENTS ===== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8" data-testid="subscriptions-section">
        <div className="glass rounded-2xl px-5 py-4 text-sm text-zinc-300 text-center max-w-3xl mx-auto">
          {t("nfc.subsExplain")}
        </div>

        <div className="flex justify-center my-10">
          <div className="glass rounded-full p-1 inline-flex" data-testid="pricing-toggle">
            {["monthly", "yearly"].map((c) => (
              <button key={c} onClick={() => setCycle(c)} data-testid={`pricing-${c}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${cycle === c ? "bg-brand-gradient text-white" : "text-zinc-400"}`}>
                {c === "monthly" ? t("pricing.monthly") : t("pricing.yearly")}
                {c === "yearly" && <span className="ml-2 text-xs text-cyan-300">-15%</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {plans.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.05}>
              <div className={`h-full rounded-3xl p-6 flex flex-col ${p.key === "pro" ? "border-brand-gradient" : "glass"}`} data-testid={`plan-${p.key}`}>
                {p.key === "pro" && <span className="self-start text-xs font-mono uppercase tracking-wider text-cyan-400 mb-2">{t("pricing.popular")}</span>}
                <h3 className="font-display font-bold text-lg text-white">{p.name}</h3>
                <p className="mt-3 font-display font-black text-3xl text-white">
                  {fmt(priceFor(p))} €
                  <span className="text-sm font-normal text-zinc-500"> {t("nfc.ttc")} /{cycle === "monthly" ? t("pricing.perMonth") : t("pricing.perYear")}</span>
                </p>
                <p className="mt-3 text-sm text-zinc-400">
                  {p.max_profiles === 1 ? t("pricing.oneCard") : <>{t("pricing.upTo")} <span className="text-white font-semibold">{p.max_profiles} {t("pricing.profiles")}</span></>}
                </p>
                <ul className="mt-5 space-y-2.5 text-sm flex-1">
                  {(Array.isArray(features) ? features : []).map((f) => (
                    <li key={f} className="flex gap-2 text-zinc-300"><Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link to="/contact" data-testid={`plan-cta-${p.key}`}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-white font-semibold px-4 py-3 rounded-full text-sm hover:opacity-90 transition-opacity duration-300">
                    {t("buttons.talkProject")} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-zinc-500 max-w-3xl mx-auto">{t("nfc.subsExample")}</p>
        <p className="mt-2 text-center text-sm text-cyan-400 flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" /> {t("pricing.accompaniment")}</p>
      </section>

      {/* ===== LIEN VERS SOLUTIONS NFC ===== */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <Reveal>
          <div className="glass rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left" data-testid="pricing-to-nfc">
            <span className="w-14 h-14 rounded-2xl bg-brand-gradient flex items-center justify-center shrink-0"><NfcIcon className="w-7 h-7 text-white" /></span>
            <div className="flex-1">
              <h3 className="font-display font-bold text-lg text-white">{t("nfc.title")}</h3>
              <p className="mt-1 text-sm text-zinc-400">{t("nfc.subtitle")} {t("nfc.billedSeparately")}</p>
            </div>
            <Link to="/nfc" data-testid="pricing-nfc-link"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white font-semibold px-5 py-3 rounded-full hover:bg-white/20 transition-colors duration-300 shrink-0">
              {t("nfc.title")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="rounded-[2.5rem] bg-brand-gradient p-12 text-center">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-white max-w-2xl mx-auto">{t("pricing.ctaSupport")}</h2>
            <div className="mt-8 flex justify-center">
              <Link to="/contact" data-testid="pricing-cta"
                className="inline-flex items-center gap-2 bg-[#0a0a12] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#141420] transition-colors duration-300">
                {t("buttons.talkProject")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
